import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { getAllUsers } from "../../api/users/user";
import { UserListingProps } from "../../utils/interface/userListing";
import { extractApiError, getSafeImageSrc } from "../../utils/util";
import defaultProfileImage from "../../assets/profile-img.svg";
import { useQuery } from "@tanstack/react-query";

interface UserSearchDropdownProps {
    value: string | undefined;
    onChange: (userId: string) => void;
    onBlur?: () => void;
    initialUserId?: string | undefined;
    touched?: boolean;
    error?: string;
    variant?: 'default' | 'page';
    label?: string;
}

const UserSearchDropdown: React.FC<UserSearchDropdownProps> = ({
    onChange,
    onBlur,
    initialUserId,
    touched = false,
    error,
    variant = 'default',
    label = 'User'
}) => {
    const [users, setUsers] = useState<UserListingProps['userList']>([]);
    const [userSearchQuery, setUserSearchQuery] = useState("");
    const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserListingProps['userList'][0] | null>(null);
    const userDropdownRef = useRef<HTMLDivElement>(null);

    // Debounce user search before querying
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedUserSearch(userSearchQuery.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [userSearchQuery]);

    const { data, isLoading: usersLoading, error: usersError } = useQuery<{
        users?: UserListingProps["userList"];
    }>({
        queryKey: ["users", "dropdown", debouncedUserSearch],
        queryFn: async () => {
            const limit = debouncedUserSearch ? 10 : 100;
            return getAllUsers(1, limit, debouncedUserSearch);
        },
    });

    useEffect(() => {
        const nextUsers = data?.users ?? [];
        setUsers(nextUsers);
        if (initialUserId) {
            const user = nextUsers.find((u) => u.id === initialUserId);
            if (user) {
                setSelectedUser(user);
            }
        }
    }, [data, initialUserId]);

    useEffect(() => {
        if (usersError) {
            toast.error(extractApiError(usersError));
        }
    }, [usersError]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setIsUserDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleUserSelect = (user: UserListingProps['userList'][0]) => {
        setSelectedUser(user);
        onChange(user.id);
        setUserSearchQuery("");
        setIsUserDropdownOpen(false);
    };

    const isPageVariant = variant === 'page';
    // Default avatar image used when user has no profile image
    const dummyAvatarUrl = defaultProfileImage;
    const displayName = selectedUser
        ? (selectedUser.fullName || `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.email)
        : '';
    const displayEmail = selectedUser?.email ?? '';
    const triggerContent = isPageVariant ? (
        <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-[40px] h-[40px] rounded-full overflow-hidden bg-slate-700">
                <img
                    src={getSafeImageSrc(selectedUser?.profileImagePreview ?? selectedUser?.profileImage, dummyAvatarUrl) || undefined}
                    alt={displayName || 'User'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = dummyAvatarUrl;
                    }}
                />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[17px] font-semibold text-white truncate">
                    {displayName || 'Select User'}
                </p>
                <p className="text-[13px] font-light truncate ">
                    {displayEmail || 'No email'}
                </p>
            </div>
        </div>
    ) : selectedUser ? (
        <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden bg-slate-700 border border-blue-500/30">
                <img
                    src={getSafeImageSrc(selectedUser.profileImagePreview ?? selectedUser.profileImage, dummyAvatarUrl) || undefined}
                    alt={displayName || 'User'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = dummyAvatarUrl;
                    }}
                />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">
                    {selectedUser.fullName || `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() || selectedUser.email}
                </p>
                <p className="text-xs text-gray-400 truncate">{selectedUser.email}</p>
            </div>
        </div>
    ) : (
        <span className="text-gray-500 text-sm">Select user...</span>
    );

    return (
        <div className="max-w-[511px]">
            <label className={`block text-base font-light mb-4 ${isPageVariant ? 'text-white' : 'text-gray-200'}`}>
                {label} {!isPageVariant && <span className="text-red-400">*</span>}
            </label>
            <div className="relative " ref={userDropdownRef}>
                <div className="relative">
                    {isPageVariant ? (
                        <button
                            type="button"
                            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                            onBlur={onBlur}
                            className={`w-full px-[23px] py-3 pr-10 rounded-[27px] text-left user-dropdown-button flex items-center gap-2 border-2 transition-all duration-200 min-h-[70px]  ${touched && error
                                ? 'border-red-500/50 bg-red-500/10'
                                : 'border-[#E761A0] bg-black/50  hover:border-[#E761A0] '
                                }`}
                        >
                            {triggerContent}
                            <div className="absolute right-[26px] top-1/2 -translate-y-1/2 pointer-events-none">
                                {usersLoading ? (
                                    <div className="w-5 h-5 border-2 border-t-transparent border-[#E761A0] rounded-full animate-spin" />
                                ) : (
                                    <svg className="w-5 h-5 text-[#E761A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                )}
                            </div>
                        </button>
                    ) : (
                        <>
                            <input
                                type="text"
                                value={selectedUser ? `${selectedUser.fullName} (${selectedUser.email})` : userSearchQuery}
                                onChange={(e) => {
                                    setUserSearchQuery(e.target.value);
                                    setIsUserDropdownOpen(true);
                                    if (selectedUser) {
                                        setSelectedUser(null);
                                        onChange("");
                                    }
                                }}
                                onFocus={() => setIsUserDropdownOpen(true)}
                                onBlur={onBlur}
                                placeholder="Search and select a user..."
                                maxLength={256}
                                className={`w-full px-4 py-3 pr-10 border-2 rounded-[27px] text-white bg-transparent focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none transition-all duration-200 placeholder-gray-500 ${touched && error
                                    ? 'border-red-500/50 bg-red-500/10'
                                    : 'border-[#E761A0] bg-black/50 hover:border-[#E761A0]'
                                    }`}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                {usersLoading ? (
                                    <div className="w-5 h-5 border-2 border-t-transparent border-[#E761A0] rounded-full animate-spin" />
                                ) : (
                                    <svg className="w-5 h-5 text-[#E761A0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {isUserDropdownOpen && (
                    <div className="absolute z-50 w-full mb-2 bottom-full left-0 bg-black/90 border-2 border-[#E761A0]/50 rounded-[18px] shadow-xl max-h-[280px] overflow-hidden flex flex-col backdrop-blur-sm">
                        {isPageVariant && (
                            <div className="p-2 border-b border-[#E761A0]/20 flex-shrink-0">
                                <input
                                    type="text"
                                    value={userSearchQuery}
                                    onChange={(e) => setUserSearchQuery(e.target.value)}
                                    placeholder="Search users..."
                                    maxLength={256}
                                    className="w-full px-3 py-[6px] text-sm rounded-[27px] border-2 border-[#E761A0]/30 bg-transparent text-white placeholder-gray-500 focus:ring-2 focus:ring-[#E761A0]/50 focus:border-[#E761A0] focus:outline-none transition-colors"
                                />
                            </div>
                        )}
                        <div className="overflow-y-auto flex-1 min-h-0">
                            {users.length > 0 ? (
                                <ul className="py-0">
                                    {users.map((user) => (
                                        <li
                                            key={user.id}
                                            onClick={() => handleUserSelect(user)}
                                            className={`px-4 py-[5px] cursor-pointer transition-colors border-l-2 border-transparent ${selectedUser?.id === user.id
                                                ? 'bg-[#E761A0]/10 border-l-[#E761A0]'
                                                : 'hover:bg-[#E761A0]/5 border-l-transparent'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden bg-slate-700 border border-[#E761A0]/40">
                                                    <img
                                                        src={getSafeImageSrc(user.profileImagePreview ?? user.profileImage, dummyAvatarUrl) || undefined}
                                                        alt={user.fullName || user.email}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = dummyAvatarUrl;
                                                        }}
                                                    />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-white truncate">
                                                        {user.fullName ||
                                                            (user.firstName || user.lastName
                                                                ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()
                                                                : "")}
                                                    </p>
                                                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                                                </div>
                                                {selectedUser?.id === user.id && (
                                                    <svg className="w-5 h-5 text-[#E761A0] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="px-4 py-10 text-center">
                                    <p className="text-sm text-gray-400">
                                        {usersLoading ? "Loading users..." : "No users found"}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {touched && error && (
                <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                </p>
            )}
        </div>
    );
};

export default UserSearchDropdown;





